import { TableCell, Toolbar } from '@material-ui/core';
import { attributes, Collection, Linked, refTo } from '@type-r/models';
import { useIO, useLinked, useModel } from '@type-r/react';
import { asCurrency, CollapsableSection, Column, Columns, CompactTextArea, Flex, FormInput, InlineTable, Loading, PopupIODialog, PrimaryButton, TableHeader, TableRow } from 'lqx-react';
import * as React from 'react';
import { CompanyModel, globalStore, Participant, PlatformCompany, RelationshipModel } from 'api';
import { execFlagForReview } from '../../api';
import { getAvailableCompanies, UnresolvedPayment } from '../../api/unresolvedPayment';
// import { CreateRelationshipDialog } from './createRelationshipDialog';
import { PickCompany } from './pickCompany';
import { ResolvePaymentRelationshipDialog } from './resolve';
import { execGetARBalance } from '../../api/common';
import { InvoiceLookupInput } from './invoiceLookupInput';
import { UnresolvedRelationship } from '../../api/unresolvedRelationship';
import { Can } from 'src/app/services/can';
import { UserEntitlementCatalogs } from 'src/app/models/UserEntitlements';

const State = attributes({
    customer : refTo( PlatformCompany ),
    supplier : refTo( PlatformCompany ),
    resolving : refTo( Boolean ),
    availableCustomers : [PlatformCompany]
});

export const PaymentDetails = ({ payment, companies, onRelationshipSelection, $supplier, $customer, showResolveDialog = true, showFlag = false } : {
    payment : UnresolvedPayment,
    companies : Collection<PlatformCompany>,
    onRelationshipSelection? : () => void,
    showResolveDialog? : Boolean,
    showFlag? : Boolean,
    $supplier? : Linked<PlatformCompany>,
    $customer? : Linked<PlatformCompany>
}) => {

    const state = useModel( State );
    const $flag = useLinked(false);

    React.useEffect(() => {
        if (payment.supplier && payment.supplier.id) {
            state.$.supplier.set(companies.get(payment.supplier.id));
        }

        if (payment.customer && payment.customer.id) {
            state.$.customer.set(companies.get(payment.customer.id));
        }
    }, []);

    React.useEffect(() => {
        if ($supplier && $customer) {
            $supplier.set(state.supplier);
            $customer.set(state.customer);   
        }
    }, [state.customer, state.supplier]);

    const isReady = useIO(async () => {
        if (payment.payorAgentId) {
            // state.availableCustomers = await getAvailableCompanies(payment.id);
        } else {
            state.availableCustomers = null;
        }
    }, [payment.id]);

    const $foundRelationship = useLinked<RelationshipModel>(null);

    const resolvePaymentOnBehalf = async () => {
        const tempRelationship = new RelationshipModel();
        tempRelationship.participants.add([
            new Participant({ companyId: state.customer.id, role: 'Customer'}), 
            new Participant({ companyId: state.supplier.id, role: 'Supplier'})]);
    };

    const $invoiceNumber = useLinked("");

    const $unresolvedRelationship = useLinked<UnresolvedRelationship>(null);

    return <>
        <CollapsableSection
            title={ 'Payment #' + payment.id }
        >
            <Columns>
                <Column>
                    <FormInput label="Payee Name" value={ payment.supplier.name } />
                    <FormInput label="Payee Bank" value={ payment.supplier.bankName } />
                    <FormInput label="Payee Account" value={ payment.supplier.account } />
                    <InvoiceLookupInput
                        $supplier={state.$.supplier} 
                        $customer={state.$.customer}
                        paymentId={payment.id}
                        customerLookup={!!payment.payorAgentId}
                        $unresolvedRelationship={$unresolvedRelationship}
                    />
                    { payment.payorAgentId ? <FormInput label="Payor Agent" value={ payment.payorAgent.name } /> :undefined }
                    <PickCompany 
                        $value={ state.$.supplier } companies={companies} 
                        filter={company => !state.customer || company.id !== state.customer.id} populatedCompany={payment.supplier} customer={state.customer} 
                        disabled={ !!payment.payorAgentId }    
                    />
                    {/* <p>
                        { state.supplier && state.supplier.bankAccount ? 
                            state.supplier.bankAccount.join( ',' )
                        : null }
                    </p> */}
                    { showResolveDialog && state.customer && state.supplier && payment.payorAgentId && Can(UserEntitlementCatalogs.BankEntryWrite) ? 
                        <PrimaryButton
                            onClick={() => resolvePaymentOnBehalf()}
                        >
                            Resolve
                        </PrimaryButton> 
                        : null 
                    }
                    
                </Column>

                <Column>
                    <FormInput label="Payer Name" value={ payment.customer.name } />
                    <FormInput label="Payer Bank" value={ payment.customer.bankName } />
                    <FormInput label="Payer Account" value={ payment.customer.account } />
                    <FormInput label="" aria-label="Empty" />
                    { payment.payorAgentId ? <FormInput label="" /> : undefined }
                    { isReady ? 
                        <PickCompany $value={ state.$.customer } companies={companies} 
                            filter={company => !state.supplier || company.id !== state.supplier.id} supplier={ state.supplier } populatedCompany={payment.customer} 
                            withCount={ !payment.payorAgentId } strictRelationship={ !payment.payorAgentId }
                        /> 
                        : <Loading/>
                    }
                    {showResolveDialog && Can(UserEntitlementCatalogs.BankEntryWrite) ? 
                        <PrimaryButton 
                            onClick={() => state.$.resolving.set(true)}
                            disabled={!state.customer || !state.supplier}
                        >
                            Resolve
                        </PrimaryButton> : undefined }
                    {/* { state.customer && state.customer.bankAccount ?
                        <FormInput label="Company Account" value={ state.customer.bankAccount.join( ', ' ) } />
                    : null } */}
                    
                </Column>
            </Columns>
            
            { showFlag ? 
                <Toolbar>
                    <Flex grow />
                    <PrimaryButton color = "red" onClick={ () => { $flag.true(); } }>
                    Flag
                    </PrimaryButton>
                </Toolbar> : undefined 
            }
        </CollapsableSection>

        <CollapsableSection
            title="Unstructured Remittance Info"
        >
            { payment.remittanceInfoUnstructured }
        </CollapsableSection>
        <CollapsableSection
            title="Additional Entry Info"
        >
            { payment.additionalEntryInfo }
        </CollapsableSection>
        { state.resolving && showResolveDialog ?
            onRelationshipSelection ? onRelationshipSelection() :
            <ResolvePaymentRelationshipDialog payment={payment} $resolving={state.$.resolving } customer={ state.customer } supplier={ state.supplier }/>
        : null }

        { $flag.value ? <FlagPaymentForReviewDialog payment={ payment } $isOpen={ $flag } />  : null }

    </>
}

export const FlagPaymentForReviewDialog = ({ $isOpen, payment }) => {
  const $comment = useLinked("Parsing logic did not work as expected.");
  return (
    <PopupIODialog
      title="Flag for review"
      $isOpen={ $isOpen }
      actionLabel="Yes"
      success="Payment Flagged"
      doIO={ async () => { execFlagForReview(payment.id, true, $comment.value) }}
      hideTitleOnSuccess
    >
      <CompactTextArea readOnly label="Comment from administrator" $value={ $comment } />
    </PopupIODialog>
  );
};

export const ResolvePanel = ({ supplier, customer, $resolving, companies, customerInfo = null, supplierInfo = null, disableCreateRelationship = false, autoPick = false, resolveBtnLabel = 'Resolve', highlightedRelationship = null  }) => {
    const relationships = highlightedRelationship ?
        [globalStore.getRelationshipById(highlightedRelationship)] :
        globalStore.relationshipsByCompanies( supplier, customer );
    const $createRelationship = useLinked(false);

    autoPick = autoPick && !highlightedRelationship;

    React.useEffect(() => {
        if (autoPick) {
            if (relationships.length === 1) {
                $resolving.set(relationships[0]);
            }
        }
    }, [relationships]);

    const $arBalances = useLinked(null);

    const relationshipsByCompanies = globalStore
        .relationshipsByCompanies(supplier, customer);

    const isReady = useIO( async () => {
        const conversations = globalStore.conversations
            .filter(conv => relationshipsByCompanies.map(comp => Number(comp.id)).includes(conv.relationshipId))

        const conversationsByRelationship = conversations
            .reduce((acc, conversation) => {
                if (!acc[conversation.relationshipId]) {
                    acc[conversation.relationshipId] = [];
                }
                acc[conversation.relationshipId].push(conversation.id);

                return acc;
            }, {});

        const balances = await execGetARBalance(Number(supplier.id), Number(customer.id), conversations.map(conv => conv.id));

        const arBalances = {};
        for (let [relationshipId, conversationIds] of Object.entries(conversationsByRelationship)) {
            const convIds = conversationIds as number[];
            const totalByRelationsip = Object.keys(balances)
                .filter(key => convIds.includes(Number(key)))
                .reduce((total, key) => {
                    return total + balances[key];
                }, 0)
            arBalances[relationshipId] = totalByRelationsip;
        }

        $arBalances.set(arBalances);
    }, [supplier, customer]);


    const resolving = $resolving.value;

    return (
        <CollapsableSection
            title="Resolve relationship"
        >
            { autoPick && resolving ? 
                <Columns>
                    <Column>
                        <FormInput label="Selected Customer Rel. Id" value={resolving.Customer.externalRelationshipId} />
                        <FormInput label="AR Balance" value={ asCurrency($arBalances.value ? $arBalances.value[resolving.id] || 0 : 0) } />
                    </Column>
                </Columns> :
            relationships.length ?
                <>
                    <p>
                        { relationships.length } relationships found. Pick one to resolve.
                    </p>
                    <InlineTable>
                        <TableHeader>
                            <TableCell>Id</TableCell>
                            <TableCell>Customer Rel. Id</TableCell>
                            <TableCell>Supplier Ext. Id</TableCell>
                            <TableCell>AR Balance</TableCell>
                            <TableCell></TableCell>
                        </TableHeader>
                        { relationshipsByCompanies
                            .map( x => 
                                {
                                    return <TableRow className={ highlightedRelationship && highlightedRelationship.id === x.id ? 'dark' : '' }>
                                        <TableCell>
                                            {x.id}
                                        </TableCell>
                                        <TableCell>
                                            {x.Customer.externalRelationshipId}
                                        </TableCell>
                                        <TableCell>
                                            {x.supplierExternalId}
                                        </TableCell>
                                        <TableCell>
                                            {isReady ? 
                                                asCurrency($arBalances.value && $arBalances.value[x.id] ? $arBalances.value[x.id] : '0') 
                                            : <Loading />}
                                        </TableCell>
                                        <TableCell className="fit-width">
                                            <PrimaryButton
                                                onClick={() => $resolving.set(x)}
                                            >
                                                {resolveBtnLabel}
                                            </PrimaryButton>
                                        </TableCell>
                                    </TableRow>;
                                }
                                
                            )
                        }

                    </InlineTable>
                </>
            :
                <>
                    <Flex center>
                        {/* No relationships configured
                        { disableCreateRelationship ? undefined : <PrimaryButton onClick={() => $createRelationship.true()}>Create Relationship</PrimaryButton> }

                        { $createRelationship.value ?
                            <CreateRelationshipDialog
                                $isOpen={ $createRelationship }
                                $relationship={$resolving}
                                supplier={supplier}
                                customer={customer}
                                customerInfo={customerInfo}
                                supplierInfo={supplierInfo}
                                companies={companies}
                            />
                        : null } */}
                    </Flex>
                </>
            }
        </CollapsableSection>
    )
}